export default function UnderstandingHoardingSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Understanding <span className="text-teal-600">Hoarding Situations</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Hoarding is a complex issue that affects millions of families. We approach every situation with empathy, respect, and professionalism.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/2415ca3b1ee13ec905d80ce0d764f808.jpg"
              alt="Understanding hoarding challenges"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What Defines a Hoarder House?</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              A hoarder house is a property where excessive accumulation of items has created living conditions that may be unsafe, unsanitary, or simply overwhelming. These situations often develop over many years and can include:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                <span className="text-gray-700"><strong>Excessive clutter</strong> that blocks walkways, exits, or entire rooms</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                <span className="text-gray-700"><strong>Accumulated possessions</strong> including papers, clothing, containers, or collectibles</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                <span className="text-gray-700"><strong>Difficulty discarding items</strong> regardless of their actual value</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                <span className="text-gray-700"><strong>Compromised living spaces</strong> where rooms can't be used for their intended purpose</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                <span className="text-gray-700"><strong>Potential health hazards</strong> from dust, mold, pests, or blocked ventilation</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                <span className="text-gray-700"><strong>Structural concerns</strong> from excessive weight or neglected maintenance</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-10">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Common Scenarios We Handle</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-heart-line text-2xl text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">Inherited Properties</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                You've inherited a home from a loved one who struggled with hoarding. The thought of sorting through decades of belongings is overwhelming, and you need a solution that's both respectful and efficient.
              </p>
            </div>

            <div>
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-hospital-line text-2xl text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">Assisted Living Transitions</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                A family member is moving to assisted living or a care facility, and their home is filled with accumulated items. You need to sell quickly but don't have the time or resources to clean out the property.
              </p>
            </div>

            <div>
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-heart-line text-2xl text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">Personal Circumstances</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                You're dealing with your own hoarding challenges and need to sell your home. You want a buyer who understands your situation and won't judge you for the condition of your property.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
